import React from 'react'
import { Alert, Button, Card, CardBody, CardHeader, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Input, Label, Row, Table, UncontrolledDropdown } from 'reactstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FModal, LoadingSpinner } from './../../components'
import Paginator from './../../components/Pagination/V2/Paginator'
import CashMahanService from './../../services/cashMahan/CashMahanService'
import CashMahanStatusBadge from './CashMahanStatusBadge'
import { CashMahanEmpty, CashMahanError } from './CashMahanStates'
import {
  WITHDRAWAL_STATUSES,
  apiErrorMessage,
  completeWithdrawalPayload,
  displayText,
  extractCollection,
  extractPagination,
  formatDate,
  formatMoney,
  isForbidden,
  isSuccess,
  optionalNotePayload,
  payoutDetails,
  recordId,
  requiredNotePayload,
  rtlProps,
  technicianId,
  technicianLabel,
  unwrapData,
  withdrawalActions
} from './cashMahanUtils'

const ACTION_COPY = {
  approve: {
    title: 'Approve withdrawal',
    message: 'Approving does not transfer money. It only marks this request as approved so the team can transfer funds later.'
  },
  reject: {
    title: 'Reject withdrawal',
    message: 'Rejecting releases the reserved balance back to the technician. This does not transfer money.'
  },
  processing: {
    title: 'Mark as processing',
    message: 'Use this after the team has started the manual transfer. Approving still does not move money by itself.'
  },
  complete: {
    title: 'Complete withdrawal',
    message: 'Complete only after the team has manually transferred the funds. Enter the transfer reference from the bank or wallet receipt.'
  },
  fail: {
    title: 'Fail withdrawal',
    message: 'Failing releases the reserved balance back to the technician. Use this if the manual transfer could not be completed.'
  }
}

export default class Withdrawals extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      submitting: false,
      error: '',
      items: [],
      noResult: false,
      previousPages: [],
      nextPages: [],
      currentPage: 1,
      perPage: 15,
      status: '',
      fixerId: '',
      payoutType: '',
      fromDate: null,
      toDate: null,
      from: '',
      to: '',
      detailsModal: false,
      actionModal: false,
      actionType: '',
      selected: null,
      details: null,
      transferReference: '',
      note: ''
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0)
    this.load()
  }

  queryParams() {
    return {
      page: this.state.currentPage,
      perPage: this.state.perPage,
      status: this.state.status,
      fixerId: this.state.fixerId,
      payoutType: this.state.payoutType,
      from: this.state.from,
      to: this.state.to
    }
  }

  load() {
    this.setState({ loading: true, error: '' })
    CashMahanService.getWithdrawals(this.queryParams(), (res) => {
      if (isForbidden(res)) {
        this.setState({ loading: false })
        this.props.history.push('/403')
        return
      }
      if (!isSuccess(res)) {
        this.setState({ loading: false, error: apiErrorMessage(res), items: [], noResult: false })
        return
      }
      const data = unwrapData(res)
      const items = extractCollection(data, 'withdrawals')
      const pagination = extractPagination(data)
      this.setState({
        loading: false,
        error: '',
        items,
        noResult: !items.length,
        previousPages: pagination.previousPages,
        nextPages: pagination.nextPages,
        currentPage: pagination.currentPage || this.state.currentPage
      })
    })
  }

  applyFilter() {
    this.setState({ currentPage: 1 }, () => this.load())
  }

  resetFilter() {
    this.setState({
      status: '',
      fixerId: '',
      payoutType: '',
      fromDate: null,
      toDate: null,
      from: '',
      to: '',
      currentPage: 1
    }, () => this.load())
  }

  changePage(page) {
    this.setState({ currentPage: page }, () => this.load())
  }

  handleDate(type, date) {
    const iso = date ? new Date(date + ' Z').toISOString().split('T')[0] : ''
    if (type === 'from') {
      this.setState({ fromDate: date, from: iso })
    } else {
      this.setState({ toDate: date, to: iso })
    }
  }

  openDetails(item) {
    const id = recordId(item)
    this.setState({ loading: true, selected: item, detailsModal: true, details: item })
    CashMahanService.getWithdrawal(id, (res) => {
      this.setState({ loading: false })
      if (isForbidden(res)) {
        this.props.history.push('/403')
        return
      }
      if (!isSuccess(res)) {
        alert(apiErrorMessage(res))
        return
      }
      const data = unwrapData(res)
      this.setState({ details: data.withdrawal || data })
    })
  }

  openAction(item, actionType) {
    this.setState({
      actionModal: true,
      actionType,
      selected: item,
      transferReference: '',
      note: ''
    })
  }

  needsNoteField() {
    return ['approve', 'reject', 'processing', 'complete', 'fail'].indexOf(this.state.actionType) !== -1
  }

  noteRequired() {
    return this.state.actionType === 'reject' || this.state.actionType === 'fail'
  }

  runAction() {
    if (this.state.submitting) {
      return
    }
    const id = recordId(this.state.selected)
    const action = this.state.actionType
    if (this.noteRequired() && !String(this.state.note || '').trim()) {
      alert('Enter a note')
      return
    }
    if (action === 'complete' && !String(this.state.transferReference || '').trim()) {
      alert('Enter the transfer reference')
      return
    }

    const handlers = {
      approve: (cb) => CashMahanService.approveWithdrawal(id, optionalNotePayload(this.state.note), cb),
      processing: (cb) => CashMahanService.processingWithdrawal(id, optionalNotePayload(this.state.note), cb),
      reject: (cb) => CashMahanService.rejectWithdrawal(id, requiredNotePayload(this.state.note), cb),
      fail: (cb) => CashMahanService.failWithdrawal(id, requiredNotePayload(this.state.note), cb),
      complete: (cb) => CashMahanService.completeWithdrawal(
        id,
        completeWithdrawalPayload(this.state.transferReference, this.state.note),
        cb
      )
    }

    this.setState({ submitting: true, loading: true })
    handlers[action]((res) => {
      this.setState({ submitting: false, loading: false })
      if (isForbidden(res)) {
        this.props.history.push('/403')
        return
      }
      if (!isSuccess(res)) {
        alert(apiErrorMessage(res))
        return
      }
      this.setState({ actionModal: false, selected: null, detailsModal: false }, () => this.load())
    })
  }

  renderDetails(item) {
    const row = item || {}
    return (
      <div style={{ marginLeft: 10 }}>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Technician: <strong {...rtlProps(technicianLabel(row))}>{technicianLabel(row)}</strong></span></p>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Payout account: <strong>{payoutDetails(row)}</strong></span></p>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Requested amount: <strong>{formatMoney(row.requestedAmount)}</strong></span></p>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Fees: <strong>{formatMoney(row.fees)}</strong></span></p>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Net transfer amount: <strong>{formatMoney(row.netTransferAmount)}</strong></span></p>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Status: <CashMahanStatusBadge item={row} /></span></p>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Request date: <strong>{formatDate(row.requestedAt || row.createdAt)}</strong></span></p>
        <p><i className="icon-arrow-right-circle"></i><span style={{ marginLeft: 6 }}>Transfer reference: <strong>{displayText(row.transferReference)}</strong></span></p>
      </div>
    )
  }

  renderActionForm() {
    const copy = ACTION_COPY[this.state.actionType] || {}
    return (
      <div>
        <Alert color="warning">{copy.message}</Alert>
        {this.renderDetails(this.state.selected)}
        {this.state.actionType === 'complete' ? (
          <FormGroup>
            <Label>Transfer reference</Label>
            <Input
              value={this.state.transferReference}
              onChange={(e) => this.setState({ transferReference: e.target.value })}
              placeholder="BANK-998877"
              disabled={this.state.submitting}
            />
          </FormGroup>
        ) : null}
        {this.needsNoteField() ? (
          <FormGroup>
            <Label>{this.noteRequired() ? 'Note' : 'Note (optional)'}</Label>
            <Input
              type="textarea"
              value={this.state.note}
              onChange={(e) => this.setState({ note: e.target.value })}
              placeholder={this.noteRequired() ? 'Enter note' : 'Optional note'}
              disabled={this.state.submitting}
            />
          </FormGroup>
        ) : null}
      </div>
    )
  }

  renderFilters() {
    return (
      <div>
        <Row>
          <Col md="2">
            <FormGroup>
              <Label>Status</Label>
              <UncontrolledDropdown>
                <DropdownToggle caret className='actionsBtn' style={{ width: '100%' }}>
                  {this.state.status || 'All'}
                </DropdownToggle>
                <DropdownMenu className='scrolledMenu'>
                  <DropdownItem onClick={() => this.setState({ status: '' })}>All</DropdownItem>
                  {WITHDRAWAL_STATUSES.map((status) => (
                    <DropdownItem key={status} onClick={() => this.setState({ status })}>{status}</DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Fixer ID</Label>
              <Input value={this.state.fixerId} onChange={(e) => this.setState({ fixerId: e.target.value })} placeholder="fixerId" />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Payout type</Label>
              <Input value={this.state.payoutType} onChange={(e) => this.setState({ payoutType: e.target.value })} placeholder="payoutType" />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>From</Label>
              <div className='date'>
                <DatePicker selected={this.state.fromDate} onChange={(date) => this.handleDate('from', date)} dateFormat="dd-MM-yyyy" placeholderText="From" />
              </div>
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>To</Label>
              <div className='date'>
                <DatePicker selected={this.state.toDate} onChange={(date) => this.handleDate('to', date)} dateFormat="dd-MM-yyyy" placeholderText="To" />
              </div>
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Per page</Label>
              <Input type="select" value={this.state.perPage} onChange={(e) => this.setState({ perPage: e.target.value })}>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <Button className='actionsBtn' style={{ backgroundColor: '#3a4248', borderColor: '#3a4248', color: '#fff' }} onClick={() => this.applyFilter()}>Filter</Button>
            <Button className='actionsBtn' style={{ backgroundColor: '#a91010', borderColor: '#a91010', color: '#fff' }} onClick={() => this.resetFilter()}>Reset</Button>
            <Button className='actionsBtn' onClick={() => this.load()}>Refresh</Button>
          </Col>
        </Row>
      </div>
    )
  }

  render() {
    const actionCopy = ACTION_COPY[this.state.actionType] || { title: 'Confirm action' }
    return (
      <div className="app align-items-center">
        <Container>
          <Alert color="info">
            <strong>Withdrawal rules: </strong>
            Approving does not transfer money.
            Completing is done only after the team manually transfers funds.
            Rejecting or failing releases the reserved balance back to the technician.
          </Alert>
          {this.renderFilters()}
          <Card>
            <CardHeader className='shadow'>
              <i className="fa fa-align-justify"></i>
              <span style={{ marginLeft: 6 }}>Withdrawal management</span>
            </CardHeader>
            {this.state.error ? (
              <CashMahanError message={this.state.error} onRetry={() => this.load()} />
            ) : this.state.noResult ? (
              <CashMahanEmpty />
            ) : (
              <CardBody className='shadow'>
                <Table hover bordered striped responsive size="md">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center' }}>Technician</th>
                      <th style={{ textAlign: 'center' }}>Payout account</th>
                      <th style={{ textAlign: 'center' }}>Requested amount</th>
                      <th style={{ textAlign: 'center' }}>Fees</th>
                      <th style={{ textAlign: 'center' }}>Net transfer amount</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'center' }}>Request date</th>
                      <th style={{ textAlign: 'center' }}>Transfer reference</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.items.map((item, index) => {
                      const fixerId = technicianId(item)
                      const actions = withdrawalActions(item)
                      return (
                        <tr key={recordId(item) || index}>
                          <td style={{ textAlign: 'center', ...rtlProps(technicianLabel(item)).style }}>
                            {fixerId ? (
                              <Button className="click" style={{ backgroundColor: 'transparent', borderColor: 'transparent', fontWeight: 'bold' }} onClick={() => this.props.history.push(`/cash-mahan/technicians/${fixerId}/wallet`)}>
                                {technicianLabel(item)}
                              </Button>
                            ) : technicianLabel(item)}
                          </td>
                          <td style={{ textAlign: 'center' }}>{payoutDetails(item)}</td>
                          <td style={{ textAlign: 'center' }}>{formatMoney(item.requestedAmount)}</td>
                          <td style={{ textAlign: 'center' }}>{formatMoney(item.fees)}</td>
                          <td style={{ textAlign: 'center' }}>{formatMoney(item.netTransferAmount)}</td>
                          <td style={{ textAlign: 'center' }}><CashMahanStatusBadge item={item} /></td>
                          <td style={{ textAlign: 'center' }}>{formatDate(item.requestedAt || item.createdAt)}</td>
                          <td style={{ textAlign: 'center' }}>{displayText(item.transferReference)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <Button className='actionsBtn' onClick={() => this.openDetails(item)}>View</Button>
                            {actions.map((action) => (
                              <Button
                                key={action}
                                className='actionsBtn'
                                disabled={this.state.submitting}
                                style={action === 'reject' || action === 'fail' ? { backgroundColor: '#a91010', borderColor: '#a91010', color: '#fff' } : null}
                                onClick={() => this.openAction(item, action)}
                              >
                                {action}
                              </Button>
                            ))}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
                <Paginator prevPages={this.state.previousPages} nextPages={this.state.nextPages} notify={(page) => this.changePage(page)} />
              </CardBody>
            )}
          </Card>
          <FModal
            title="Withdrawal details"
            className="modal-lg"
            content={this.renderDetails(this.state.details || this.state.selected)}
            state={this.state.detailsModal}
            onClose={() => this.setState({ detailsModal: false, details: null })}
            noSubmitBtn={true}
          />
          <FModal
            title={actionCopy.title}
            content={this.renderActionForm()}
            state={this.state.actionModal}
            onClose={() => !this.state.submitting && this.setState({ actionModal: false })}
            onSubmit={() => this.runAction()}
          />
          <LoadingSpinner loading={this.state.loading} />
        </Container>
      </div>
    )
  }
}
